import React, { useEffect, useState } from "react";
import { fetchBillableConversions } from "../../services/billableConversionsExportService";
import { formatCurrency } from "../../utils/formatters";
import {
  buildInvoiceCsvFiles, DEFAULT_EXPORT_OPTIONS, MAX_INVOICES_PER_FILE, MAX_ROWS_PER_FILE, TERMS_DAYS, totalCents,
  weekLinesAllowed,
} from "./quickbooksInvoiceCsv";
import { QuickBooksAdvertiserPicker } from "./QuickBooksAdvertiserPicker";
import { browserDownload, fieldInputStyle as input, fieldLabelStyle as label, rangeError } from "./quickbooksDownload";
import { useExportAdvertisers } from "./useExportAdvertisers";

// The QuickBooks page's Invoice export tab, /support/quickbooks/invoice-export
// (DEV-2600): downloads the billable conversions of the advertisers support
// picks, whatever their billing method, as a QuickBooks Online invoice-import
// CSV, so accounting can invoice them by hand. Days are UTC billing-verdict
// days, the same days the auto-charge job charges on. The layout options are
// remembered in this browser; the dates always start at month to date. The
// advertiser filter starts at every advertiser on each visit and is sent with
// the request (migration 104), so only the picked advertisers are read.

export const OPTIONS_STORAGE_KEY = "cpa_ui_qbo_invoice_export_options";

// The radio choices, [value, label]. Saved options are kept only when they
// are one of these values.
const CHOICES = {
  grouping: [
    ["day", "One invoice per advertiser per day"],
    ["week", "One invoice per advertiser per week"],
    ["range", "One invoice per advertiser for the whole range"],
  ],
  lines: [["conversion", "One line per conversion"], ["day", "One line per day per CPA"], ["week", "One line per week per CPA"]],
  invoiceNumbers: [["ours", "Ours (one number per advertiser and date)"], ["quickbooks", "QuickBooks assigns them"]],
};

function loadOptions() {
  let saved = {};
  try {
    saved = JSON.parse(window.localStorage.getItem(OPTIONS_STORAGE_KEY)) || {};
  } catch {
    saved = {};
  }
  const options = { ...DEFAULT_EXPORT_OPTIONS };
  for (const [key, choices] of Object.entries(CHOICES)) {
    if (choices.some(([value]) => value === saved[key])) options[key] = saved[key];
  }
  if (Object.keys(TERMS_DAYS).includes(saved.terms)) options.terms = saved.terms;
  if (typeof saved.item === "string") options.item = saved.item;
  return options;
}

// Weekly lines on daily invoices become daily lines; the saved choice is kept,
// so switching back to weekly or range invoices brings weekly lines back.
function effectiveOptions(options) {
  return options.lines === "week" && !weekLinesAllowed(options.grouping) ? { ...options, lines: "day" } : options;
}

function saveOptions(options) {
  try {
    window.localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(options));
  } catch {
    // Storage blocked (private window): the options just are not remembered.
  }
}

// Conversions an auto-charge advertiser has not been charged for yet count on
// their billing day now and on their charge day once charged (migration 105).
function pendingChargeNote(count) {
  return count === 1
    ? "1 conversion of an auto-charge advertiser is not charged yet. It moves to its charge day once charged, so importing this range again later would invoice it twice."
    : `${count.toLocaleString("en-US")} conversions of auto-charge advertisers are not charged yet. Each moves to its charge day once charged, so importing this range again later would invoice them twice.`;
}

function summary(files, rows) {
  const total = formatCurrency(totalCents(rows) / 100);
  const count = rows.length.toLocaleString("en-US");
  const fileText = files.length === 1
    ? "1 file"
    : `${files.length} files (QuickBooks imports at most ${MAX_INVOICES_PER_FILE} invoices or ${MAX_ROWS_PER_FILE.toLocaleString("en-US")} rows per file)`;
  const totals = `${count} ${rows.length === 1 ? "conversion" : "conversions"}, ${total}.`;
  return files.length === 1
    ? `Downloaded ${fileText}: ${totals}`
    : `Ready: ${fileText}: ${totals} Download each part and import them all.`;
}

const fieldset = { border: "none", padding: 0, margin: "0 0 18px" };
const legend = { ...label, padding: 0 };
const choice = { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#151515", marginBottom: 6, cursor: "pointer" };
const hint = { fontSize: 12, color: "#626262", margin: "4px 0 0" };
const warning = { fontSize: 12, color: "#8A5A00" };

function Choices({ name, legendText, value, onChange, options, disabledValues = [] }) {
  return (
    <fieldset style={fieldset}>
      <legend style={legend}>{legendText}</legend>
      {options.map(([optionValue, text]) => (
        <label key={optionValue} style={choice}>
          <input
            type="radio"
            name={name}
            value={optionValue}
            checked={value === optionValue}
            disabled={disabledValues.includes(optionValue)}
            onChange={() => onChange(optionValue)}
          />
          {text}
        </label>
      ))}
    </fieldset>
  );
}

export function QuickBooksInvoiceExportPage({ now = () => new Date(), download = browserDownload }) {
  const [start, setStart] = useState(() => `${now().toISOString().slice(0, 8)}01`);
  const [end, setEnd] = useState(() => now().toISOString().slice(0, 10));
  const [options, setOptions] = useState(loadOptions);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { tone: "status" | "alert", text, pendingCharges? }
  // A split export: browsers block several downloads fired at once, so each
  // part gets its own button (each click is its own user gesture).
  const [parts, setParts] = useState([]);
  const { advertisers, advertisersError } = useExportAdvertisers();
  const [selected, setSelected] = useState(null); // null: every advertiser; else a Set of ids

  useEffect(() => { saveOptions(options); }, [options]);

  const setOption = (key) => (value) => setOptions((prev) => ({ ...prev, [key]: value }));
  const effective = effectiveOptions(options);
  const weekLinesOn = weekLinesAllowed(options.grouping);
  const invalid = rangeError(start, end);
  const noneSelected = selected !== null && selected.size === 0;
  const disabled = Boolean(invalid) || noneSelected || busy;
  const includesToday = !invalid && end >= now().toISOString().slice(0, 10);
  // A weekly invoice is unfinished when the range ends before its Sunday.
  const cutsLastWeek = !invalid && options.grouping === "week" && new Date(`${end}T00:00:00Z`).getUTCDay() !== 0;

  const exportCsv = async () => {
    setBusy(true);
    setResult(null);
    setParts([]);
    try {
      // A pick that covers every advertiser asks for all of them (no filter).
      const rows = await fetchBillableConversions(start, end, selected === null ? null : [...selected]);
      if (rows.length === 0) {
        setResult({
          tone: "status",
          text: selected === null ? "No billable conversions in this range." : "No billable conversions for these advertisers in this range.",
        });
        return;
      }
      const files = buildInvoiceCsvFiles(rows, { ...effective, start, end });
      if (files.length === 1) download(files[0].filename, files[0].content);
      else setParts(files);
      setResult({ tone: "status", text: summary(files, rows), pendingCharges: rows.filter((row) => row.charge_pending).length });
    } catch (err) {
      setResult({ tone: "alert", text: err?.message || "Could not export the billable conversions." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#151515", margin: "0 0 6px" }}>Invoice export</h2>
      <p style={{ color: "#626262", fontSize: 14, margin: "0 0 24px", maxWidth: "65ch" }}>
        Billable conversions of the advertisers you pick, whatever their billing method, as a CSV to import into QuickBooks Online (Settings, Import data, Invoices). A conversion already charged by card is dated on its charge day, at the amount charged, so it matches Stripe; any other is dated on the UTC day it was marked billable.
      </p>

      <div style={{ background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 12, padding: 20 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
          <div>
            <label htmlFor="export-from" style={label}>From</label>
            <input id="export-from" type="date" value={start} onChange={(e) => setStart(e.target.value)} style={input} />
          </div>
          <div>
            <label htmlFor="export-to" style={label}>To</label>
            <input id="export-to" type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={input} />
          </div>
        </div>
        <p style={{ ...hint, margin: "0 0 18px" }}>Both days are included. Defaults to month to date.</p>
        {invalid && <p style={{ fontSize: 12, color: "#B42318", margin: "-10px 0 18px" }}>{invalid}</p>}
        {includesToday && (
          <p style={{ ...warning, margin: "-10px 0 18px" }}>
            Today (UTC) is not over, so its invoices can still grow. Importing today now and again later gives the same invoice number twice; end the range yesterday to invoice only finished days.
          </p>
        )}

        <QuickBooksAdvertiserPicker
          advertisers={advertisers}
          selected={selected}
          onChange={setSelected}
          error={advertisersError}
        />
        {noneSelected && <p style={{ fontSize: 12, color: "#B42318", margin: "-10px 0 18px" }}>Pick at least one advertiser.</p>}

        {cutsLastWeek && (
          <p style={{ ...warning, margin: "-10px 0 18px" }}>
            The range ends before a Sunday, so the last week's invoices cover only part of that week. Export the rest of it later with a range that starts the next day, not the whole week again, or the same days are invoiced twice.
          </p>
        )}

        <Choices
          name="grouping"
          legendText="Invoices"
          value={options.grouping}
          onChange={setOption("grouping")}
          options={CHOICES.grouping}
        />
        <Choices
          name="lines"
          legendText="Invoice lines"
          value={effective.lines}
          onChange={setOption("lines")}
          options={CHOICES.lines}
          disabledValues={weekLinesOn ? [] : ["week"]}
        />
        {(!weekLinesOn || effective.lines === "week") && (
          <p style={{ ...hint, margin: "-12px 0 18px" }}>
            {weekLinesOn
              ? "Weeks run Monday to Sunday. A CPA change starts a new line from the day it happens."
              : "One line per week per CPA needs weekly or whole-range invoices."}
          </p>
        )}

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
          <div style={{ flex: "1 1 220px" }}>
            <label htmlFor="export-item" style={label}>Product/service</label>
            <input id="export-item" type="text" value={options.item} onChange={(e) => setOption("item")(e.target.value)} style={{ ...input, width: "100%", boxSizing: "border-box" }} />
            <p style={hint}>Must match a product or service in QuickBooks exactly. Leave empty to choose it in QuickBooks.</p>
          </div>
          <div>
            <label htmlFor="export-terms" style={label}>Terms</label>
            <select id="export-terms" value={options.terms} onChange={(e) => setOption("terms")(e.target.value)} style={input}>
              {Object.keys(TERMS_DAYS).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <Choices
          name="invoiceNumbers"
          legendText="Invoice numbers"
          value={options.invoiceNumbers}
          onChange={setOption("invoiceNumbers")}
          options={CHOICES.invoiceNumbers}
        />
        <p style={{ ...hint, margin: "-12px 0 18px" }}>
          {options.invoiceNumbers === "ours"
            ? "Turn on Custom transaction numbers in QuickBooks (Account and settings, Sales), or QuickBooks replaces them. Importing the same invoice twice then shows up as a duplicate number."
            : "The file numbers invoices 1, 2, 3 only to group their lines. Turn off Custom transaction numbers in QuickBooks (Account and settings, Sales), or those numbers are imported as they are."}
        </p>

        <button
          type="button"
          onClick={exportCsv}
          disabled={disabled}
          style={{ padding: "10px 18px", background: disabled ? "#F0F0F0" : "#F90091", color: disabled ? "#B8B8B8" : "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer" }}
        >
          {busy ? "Exporting…" : "Export CSV"}
        </button>
        {result && (
          <p role={result.tone} style={{ fontSize: 13, color: result.tone === "alert" ? "#B42318" : "#3E3E3E", margin: "14px 0 0" }}>{result.text}</p>
        )}
        {result?.pendingCharges > 0 && (
          <p style={{ ...warning, margin: "8px 0 0" }}>{pendingChargeNote(result.pendingCharges)}</p>
        )}
        {parts.length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            {parts.map((file, i) => (
              <button
                key={file.filename}
                type="button"
                onClick={() => download(file.filename, file.content)}
                style={{ padding: "8px 14px", background: "transparent", color: "#F90091", border: "1px solid #F90091", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              >
                {`Download part ${i + 1} of ${parts.length}`}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
